import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { useRefs } from "../../hooks/useRefs";
import { Actionable } from "../actionable";
import { Hidden } from "../hidden";
import { Icon } from "../icon";
import { Loader } from "../loader";
import { Text } from "../text";
import { View } from "../view";
import { classNames } from "../../utils/helpers";
import { ChevronButton } from './Chevron';
import { variantData } from './variantData';
import s from './Button.module.css';
export const ButtonBase = ({ variant = 'primary', loading, disabled, type = 'button', href, size = 'small', className, children, title, attributes, fontFamily, onClick, startIcon, endIcon, hideLabel, tabIndex, iconColor, animateOnClick = true, fontColor, disabledFontColor, as, }, ref) => {
    const rootClassName = classNames(className, s.root, variant && s[`--variant-${variant}`], size && s[`--size-${size}`], disabled && s['--disabled'], loading && s['--loading'], animateOnClick && s['--ripple']);
    const hasIcon = (React.Children.toArray(children).includes('Icon') || startIcon || endIcon) &&
        !(variant === 'super');
    if (!hasIcon && hideLabel) {
        hideLabel = {}; //If hideLabel is not supported, silently ignore it
    }
    const renderIcon = (position) => {
        const isStartValid = position === 'start' && startIcon;
        const isStartValidOld = position === 'start';
        const isEndValid = position === 'end' && endIcon;
        const isEndValidOld = position === 'end';
        const isInvalid = !(isStartValid || isStartValidOld || isEndValid || isEndValidOld);
        const isInvalidButton = !!(variant === 'super');
        const iconOnly = !!((startIcon || endIcon) && !children);
        if (isInvalid || isInvalidButton || (loading && variant != 'icon'))
            return null;
        const iconClassName = classNames(variant !== 'stacked-icon' && s.icon, position === 'end' && s['--icon-position-end'], variant === 'icon' && s['icon-only'], variant === 'pill' || variant === 'pill-secondary' ? s['--pill-icon'] : null);
        const iconSizes = {
            large: 6,
            small: 5,
        };
        const iconSize = size && (variant === 'pill' || variant === 'pill-secondary')
            ? iconSizes['small']
            : iconSizes[size];
        const icon = position === 'start' ? startIcon : endIcon;
        const iconBackgroundColor = () => {
            if (iconColor)
                return iconColor;
            if (variant === 'stacked-icon')
                return 'gray-600';
            if (variant === 'pill')
                return 'secondary';
            if (variant === 'pill-secondary')
                return 'primary';
            return 'primary';
        };
        return icon ? (_jsx(Icon, { className: iconClassName, title: title, svg: icon, color: iconBackgroundColor(), size: iconSize, autoWidth: true, attributes: {
                'aria-hidden': !iconOnly,
            } })) : null;
    };
    const renderLoader = () => {
        const loaderColor = variant === 'tertiary' ||
            variant === 'secondary' ||
            variant === 'ghost' ||
            variant === 'pill-secondary'
            ? 'primary'
            : 'secondary';
        if (variant === 'chevron' || variant.endsWith('icon') || disabled)
            return null;
        return _jsx(Loader, { variant: "inline", color: loaderColor });
    };
    const btnAttrs = Object.assign({ disabled, 'data-testid': 'st-button', 'aria-label': loading
            ? 'Loading'
            : title ||
                (typeof children === 'string' || typeof children === 'number'
                    ? String(children)
                    : undefined) }, attributes);
    const hasValidProps = () => {
        switch (variant) {
            case 'icon':
                return true;
            case 'stacked-icon':
                return loading ? false : true;
            default:
                return !loading || disabled;
        }
    };
    const renderChildren = () => {
        var _a, _b, _c, _d, _e;
        if (variant === 'chevron') {
            return (_jsx(ChevronButton, { startIcon: startIcon, children: _jsx(Hidden, { hide: hideLabel, children: children }) }));
        }
        return (_jsxs(View, { direction: variant === 'stacked-icon' ? 'column' : 'row', gap: 0.5, justify: "center", align: "center", height: "100%", children: [startIcon && renderIcon('start'), children && hasValidProps() ? (_jsx(Hidden, { hide: hideLabel, children: _jsx(Text, { className: s.text, textCase: (_a = variantData[variant]) === null || _a === void 0 ? void 0 : _a.textCase, weight: (_b = variantData[variant]) === null || _b === void 0 ? void 0 : _b.weight, size: (_c = variantData[variant]) === null || _c === void 0 ? void 0 : _c.fontSize[size], color: (fontColor && disabled ? disabledFontColor : fontColor) ||
                            ((_d = variantData[`${disabled ? 'disabled' : variant}`]) === null || _d === void 0 ? void 0 : _d.color), fontFamily: fontFamily || ((_e = variantData[variant]) === null || _e === void 0 ? void 0 : _e.fontFamily), align: "center", children: children }) })) : null, endIcon && renderIcon('end')] }));
    };
    if (children && variant === 'icon') {
        // eslint-disable-next-line no-console
        console.warn("The 'icon' variant of Button is self closing. No children should be passed.");
    }
    const buttonRef = React.useRef(null);
    React.useEffect(() => {
        if (!animateOnClick)
            return;
        const rippleClass = s['--ripple--active'];
        const button = buttonRef.current;
        const handleClick = () => {
            if (button) {
                // Re-flow hack to restart the animation
                // https://www.harrytheo.com/blog/2021/02/restart-a-css-animation-with-javascript/
                void button.offsetWidth;
                button.classList.add(rippleClass);
                setTimeout(() => {
                    button.classList.remove(rippleClass);
                }, 1000);
            }
        };
        button === null || button === void 0 ? void 0 : button.addEventListener('click', handleClick);
        return () => {
            button === null || button === void 0 ? void 0 : button.removeEventListener('click', handleClick);
        };
    }, [animateOnClick]);
    return (_jsxs(Actionable, { disabled: disabled || loading, className: rootClassName, attributes: btnAttrs, tabIndex: tabIndex, type: type, onClick: onClick, href: href, ref: useRefs([buttonRef, ref]), as: as, title: title, children: [loading && renderLoader(), renderChildren()] }));
};
export const Button = React.forwardRef(ButtonBase);
